import type { TaskState } from '../../api/rmf-client';
import type { PaginatedTaskCompletions, TaskCompletionRecord } from '../../api/v1';
import { locationFromPlaces, type TaskLocation } from './task-location';
import { completionDistance, type TaskDistanceView } from '../../api/task-distances';
import { toCleanMode, type CleanMode } from './clean-modes';

/**
 * A finished task as the Tasks tab's "Older tasks" shows it.
 *
 * Read from the durable completion record, not the live feed, so it is
 * a SUMMARY: there are no phases, no failure reason, no live estimate,
 * and nothing about it can be controlled. A column this record cannot
 * fill shows a dash rather than borrowing a guess from somewhere else.
 */
export interface OlderTask {
  id: string;
  /** `completed` / `failed` / `cancelled`; kept as the server sent it. */
  status: string;
  type: string;
  assignedRobot?: string;
  assignedFleet?: string;
  triggeredBy?: string;
  submittedAtMs: number;
  startedAtMs?: number;
  completedAtMs: number;
  /** Finish minus start, as the live table computes it. Undefined when
   *  the task never started (cancelled while queued, no bids). */
  durationSec?: number;
  location: TaskLocation | null;
  visitedPlaces: string[];
  cleanZone?: string;
  /** A clean's mode, from its dispatch intent (`clean_mode`); undefined
   *  when unknown, which reads as the vacuum every pre-mode clean was. */
  cleanMode?: CleanMode;
  /** The platform closed this record out; no robot reported it. */
  synthetic: boolean;
  rmfStatus?: string;
  settledBy?: string;
  /** The row was healed from this state (a false `failed`, usually). */
  correctedFrom?: string;
  /** Distance and cleaning time, from the record's own copy of them;
   *  null when nothing was measured (see `completionDistance`). */
  distance: TaskDistanceView | null;
  /** Area the robot reports it cleaned, m²; undefined when it reported none. */
  cleanedAreaM2?: number;
}

function present(v: string | null | undefined): string | undefined {
  return v === null || v === undefined || v === '' ? undefined : v;
}

/**
 * Where an older task went. A clean names its zone on the record
 * (`clean_zone`, resolved from the dispatch request when it ended),
 * because a clean's phases name no place; everything else uses the places
 * its phases named, shaped exactly as the live Location column shapes
 * them.
 */
function olderLocation(r: TaskCompletionRecord): TaskLocation | null {
  const zone = present(r.clean_zone);
  if (r.category === 'clean' && zone) return { text: zone, full: zone };
  return locationFromPlaces(r.category, r.visited_places ?? []);
}

export function adaptCompletion(r: TaskCompletionRecord): OlderTask {
  const startedAtMs = r.started_at_ms ?? undefined;
  return {
    id: r.task_id.task,
    status: r.state,
    type: r.category,
    assignedRobot: present(r.assigned_robot),
    assignedFleet: present(r.assigned_fleet),
    triggeredBy: present(r.triggered_by),
    submittedAtMs: r.submitted_at_ms,
    startedAtMs,
    completedAtMs: r.completed_at_ms,
    durationSec:
      startedAtMs !== undefined
        ? Math.max(0, Math.round((r.completed_at_ms - startedAtMs) / 1000))
        : undefined,
    location: olderLocation(r),
    visitedPlaces: [...(r.visited_places ?? [])],
    cleanZone: present(r.clean_zone),
    // `toCleanMode`: an api-server from before the rename says `wet_scrub`.
    cleanMode: toCleanMode(r.clean_mode),
    synthetic: r.synthetic === true,
    rmfStatus: present(r.rmf_status),
    settledBy: present(r.settled_by),
    correctedFrom: present(r.corrected_from),
    distance: completionDistance(r),
    cleanedAreaM2:
      typeof r.cleaned_area_m2 === 'number' && Number.isFinite(r.cleaned_area_m2)
        ? r.cleaned_area_m2
        : undefined,
  };
}

/**
 * Where the older view's FIRST page starts: just past the oldest finish
 * the live list still holds.
 *
 * WITHOUT THIS THE BUTTON WOULD LOOK BROKEN. The durable record holds
 * every task the live list holds too, and pages newest-first; with a
 * fortnight live at sixty tasks a day, the first seventeen pages would be
 * nothing but tasks already on screen, each dropped as a duplicate, and
 * "Load older" would appear to do nothing seventeen times.
 *
 * `+ 1` because the server's bound is exclusive and a task that finished
 * in that same millisecond may not be in the live list.
 *
 * Only ENDED live tasks count: a queued or active task has no finish, and
 * a long-queued one must not drag the start back to the day it was
 * booked. Null when nothing live has ended -- a new site, or a live store
 * that was emptied -- and the older view then starts from the newest
 * finish on record, which is exactly what has gone missing from the live
 * list.
 */
export function olderStartBeforeMs(live: ReadonlyArray<TaskState>): number | null {
  let oldest: number | null = null;
  for (const t of live) {
    if (t.status !== 'completed' && t.status !== 'failed' && t.status !== 'cancelled') continue;
    const at = t.completedAtMs;
    if (typeof at !== 'number' || !Number.isFinite(at)) continue;
    if (oldest === null || at < oldest) oldest = at;
  }
  return oldest === null ? null : oldest + 1;
}

/**
 * The loaded pages as rows, newest finish first, without any task the
 * live list already shows (a task belongs on one list, and the live one
 * has the controls) and without repeating one across pages.
 */
export function olderRowsFrom(
  pages: ReadonlyArray<PaginatedTaskCompletions> | undefined,
  liveIds: ReadonlySet<string>,
): OlderTask[] {
  const seen = new Set<string>();
  const out: OlderTask[] = [];
  for (const page of pages ?? []) {
    for (const record of page.entries) {
      const id = record.task_id.task;
      if (liveIds.has(id) || seen.has(id)) continue;
      seen.add(id);
      out.push(adaptCompletion(record));
    }
  }
  return out;
}
